import React, { useState, useCallback } from 'react';
import { GitFile, GitFileStatus, GitBranch } from '../../hooks/useGit';

type SourceControlPanelProps = {
  branch: string;
  branches: GitBranch[];
  ahead: number;
  behind: number;
  error: string | null;
  stagedFiles: GitFile[];
  unstagedFiles: GitFile[];
  commitMessage: string;
  isCommitting: boolean;
  isPushing: boolean;
  isPulling: boolean;
  isFetching: boolean;
  isSyncing: boolean;
  onCommitMessageChange: (message: string) => void;
  onStageFile: (path: string) => void;
  onUnstageFile: (path: string) => void;
  onStageAll: () => void;
  onUnstageAll: () => void;
  onDiscardFile: (path: string) => void;
  onCommit: () => void;
  onPush: () => void;
  onPull: () => void;
  onFetch: () => void;
  onSync: () => void;
  onSwitchBranch: (branch: string) => void;
  onCreateBranch: (name: string) => void;
  onDeleteBranch: (name: string) => void;
  onOpenFile: (path: string) => void;
  onShowDiff: (path: string) => void;
  getStatusIcon: (status: GitFileStatus) => string;
  getStatusColor: (status: GitFileStatus) => string;
};

export const SourceControlPanel = React.memo(function SourceControlPanel({
  branch,
  branches,
  ahead,
  behind,
  error,
  stagedFiles,
  unstagedFiles,
  commitMessage,
  isCommitting,
  isPushing,
  isPulling,
  isFetching,
  isSyncing,
  onCommitMessageChange,
  onStageFile,
  onUnstageFile,
  onStageAll,
  onUnstageAll,
  onDiscardFile,
  onCommit,
  onPush,
  onPull,
  onFetch,
  onSync,
  onSwitchBranch,
  onCreateBranch,
  onDeleteBranch,
  onOpenFile,
  onShowDiff,
  getStatusIcon,
  getStatusColor,
}: SourceControlPanelProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [stagedCollapsed, setStagedCollapsed] = useState(false);
  const [changesCollapsed, setChangesCollapsed] = useState(false);
  const [branchMenuOpen, setBranchMenuOpen] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [showNewBranchInput, setShowNewBranchInput] = useState(false);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onCommit();
    }
  }, [onCommit]);

  const totalChanges = stagedFiles.length + unstagedFiles.length;

  return (
    <div className="source-control-panel">
      <div
        className="source-control-header"
        onClick={() => setCollapsed(!collapsed)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setCollapsed(!collapsed);
          }
        }}
      >
        <span className={`source-control-collapse-icon ${collapsed ? '' : 'expanded'}`}>
          ▶
        </span>
        <span className="source-control-title">SOURCE CONTROL</span>
        <div
          className="source-control-branch-selector"
          onClick={(e) => {
            e.stopPropagation();
            setBranchMenuOpen(!branchMenuOpen);
          }}
          role="button"
          tabIndex={0}
          title="Switch branch"
        >
          <span className="branch-icon">⎇</span>
          <span className="source-control-branch">{branch}</span>
          {(ahead > 0 || behind > 0) && (
            <span className="sync-status">
              {behind > 0 && <span className="behind">↓{behind}</span>}
              {ahead > 0 && <span className="ahead">↑{ahead}</span>}
            </span>
          )}
          <span className="branch-dropdown-icon">▼</span>
        </div>
        {totalChanges > 0 && (
          <span className="source-control-badge">{totalChanges}</span>
        )}
      </div>

      {/* Branch dropdown menu */}
      {branchMenuOpen && (
        <div className="branch-menu" onClick={(e) => e.stopPropagation()}>
          <div className="branch-menu-header">
            <span>Branches</span>
            <button
              className="branch-menu-action"
              onClick={() => {
                setShowNewBranchInput(true);
                setNewBranchName('');
              }}
              title="Create new branch"
            >
              +
            </button>
          </div>
          {showNewBranchInput && (
            <div className="new-branch-input-row">
              <input
                type="text"
                className="new-branch-input"
                placeholder="New branch name..."
                value={newBranchName}
                onChange={(e) => setNewBranchName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newBranchName.trim()) {
                    onCreateBranch(newBranchName.trim());
                    setNewBranchName('');
                    setShowNewBranchInput(false);
                    setBranchMenuOpen(false);
                  }
                  if (e.key === 'Escape') {
                    setShowNewBranchInput(false);
                  }
                }}
                autoFocus
              />
              <button
                className="new-branch-confirm"
                onClick={() => {
                  if (newBranchName.trim()) {
                    onCreateBranch(newBranchName.trim());
                    setNewBranchName('');
                    setShowNewBranchInput(false);
                    setBranchMenuOpen(false);
                  }
                }}
              >
                ✓
              </button>
            </div>
          )}
          <div className="branch-list">
            {branches.map((b) => (
              <div
                key={b.name}
                className={`branch-item ${b.current ? 'current' : ''}`}
                onClick={() => {
                  if (!b.current) {
                    onSwitchBranch(b.name);
                  }
                  setBranchMenuOpen(false);
                }}
              >
                <span className="branch-name">
                  {b.current && '✓ '}{b.name}
                </span>
                {b.remote && <span className="branch-remote">{b.remote}</span>}
                {!b.current && (
                  <button
                    className="branch-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(`Delete branch "${b.name}"?`)) {
                        onDeleteBranch(b.name);
                      }
                    }}
                    title="Delete branch"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            {branches.length === 0 && (
              <div className="branch-empty">No branches available</div>
            )}
          </div>
          <div
            className="branch-menu-backdrop"
            onClick={() => setBranchMenuOpen(false)}
          />
        </div>
      )}

      {!collapsed && (
        <div className="source-control-content">
          {/* Error message */}
          {error && (
            <div className="git-error-message">
              <span className="error-icon">⚠</span>
              <span className="error-text">{error}</span>
            </div>
          )}

          {/* Git actions toolbar */}
          <div className="git-actions-toolbar">
            <button
              className="git-action-btn"
              onClick={onFetch}
              disabled={isFetching}
              title="Fetch from remote"
            >
              {isFetching ? '...' : '↓'} Fetch
            </button>
            <button
              className="git-action-btn"
              onClick={onPull}
              disabled={isPulling}
              title="Pull from remote"
            >
              {isPulling ? '...' : '⇣'} Pull
              {behind > 0 && <span className="action-badge">{behind}</span>}
            </button>
            <button
              className="git-action-btn"
              onClick={onPush}
              disabled={isPushing}
              title="Push to remote"
            >
              {isPushing ? '...' : '⇡'} Push
              {ahead > 0 && <span className="action-badge">{ahead}</span>}
            </button>
            <button
              className="git-action-btn sync-btn"
              onClick={onSync}
              disabled={isSyncing}
              title="Sync (Pull + Push)"
            >
              {isSyncing ? '...' : '⟳'} Sync
            </button>
          </div>

          {/* Commit message input */}
          <div className="commit-input-container">
            <textarea
              className="commit-message-input"
              placeholder="Commit message (Cmd+Enter to commit)"
              value={commitMessage}
              onChange={(e) => onCommitMessageChange(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={3}
            />
            <button
              className="commit-button"
              onClick={onCommit}
              disabled={isCommitting || stagedFiles.length === 0 || !commitMessage.trim()}
              title={stagedFiles.length === 0 ? 'Stage changes first' : 'Commit staged changes'}
            >
              {isCommitting ? 'Committing...' : 'Commit'}
            </button>
          </div>

          {/* Staged Changes */}
          {stagedFiles.length > 0 && (
            <div className="file-section">
              <div
                className="file-section-header"
                onClick={() => setStagedCollapsed(!stagedCollapsed)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setStagedCollapsed(!stagedCollapsed);
                  }
                }}
              >
                <span className={`section-collapse-icon ${stagedCollapsed ? '' : 'expanded'}`}>
                  ▶
                </span>
                <span className="section-title">Staged Changes</span>
                <span className="section-count">{stagedFiles.length}</span>
                <button
                  className="section-action"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnstageAll();
                  }}
                  title="Unstage All"
                >
                  −
                </button>
              </div>
              {!stagedCollapsed && (
                <div className="file-list">
                  {stagedFiles.map((file) => (
                    <div key={file.path} className="file-item staged">
                      <span
                        className="file-status"
                        style={{ color: getStatusColor(file.status) }}
                        title={file.status}
                      >
                        {getStatusIcon(file.status)}
                      </span>
                      <span
                        className="file-path"
                        onClick={() => onOpenFile(file.path)}
                        title={file.path}
                      >
                        {file.path.split('/').pop()}
                      </span>
                      <span className="file-dir">{file.path.split('/').slice(0, -1).join('/')}</span>
                      <div className="file-actions">
                        <button
                          className="file-action"
                          onClick={() => onShowDiff(file.path)}
                          title="Show Diff"
                        >
                          ⇆
                        </button>
                        <button
                          className="file-action"
                          onClick={() => onUnstageFile(file.path)}
                          title="Unstage"
                        >
                          −
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Changes (unstaged) */}
          {unstagedFiles.length > 0 && (
            <div className="file-section">
              <div
                className="file-section-header"
                onClick={() => setChangesCollapsed(!changesCollapsed)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setChangesCollapsed(!changesCollapsed);
                  }
                }}
              >
                <span className={`section-collapse-icon ${changesCollapsed ? '' : 'expanded'}`}>
                  ▶
                </span>
                <span className="section-title">Changes</span>
                <span className="section-count">{unstagedFiles.length}</span>
                <button
                  className="section-action"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStageAll();
                  }}
                  title="Stage All"
                >
                  +
                </button>
              </div>
              {!changesCollapsed && (
                <div className="file-list">
                  {unstagedFiles.map((file) => (
                    <div key={file.path} className="file-item">
                      <span
                        className="file-status"
                        style={{ color: getStatusColor(file.status) }}
                        title={file.status}
                      >
                        {getStatusIcon(file.status)}
                      </span>
                      <span
                        className="file-path"
                        onClick={() => onOpenFile(file.path)}
                        title={file.path}
                      >
                        {file.path.split('/').pop()}
                      </span>
                      <span className="file-dir">{file.path.split('/').slice(0, -1).join('/')}</span>
                      <div className="file-actions">
                        <button
                          className="file-action"
                          onClick={() => onShowDiff(file.path)}
                          title="Show Diff"
                        >
                          ⇆
                        </button>
                        <button
                          className="file-action"
                          onClick={() => onDiscardFile(file.path)}
                          title="Discard Changes"
                        >
                          ↩
                        </button>
                        <button
                          className="file-action"
                          onClick={() => onStageFile(file.path)}
                          title="Stage"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Empty state */}
          {totalChanges === 0 && (
            <div className="source-control-empty">
              No changes detected
            </div>
          )}
        </div>
      )}
    </div>
  );
});
